import { motion, useReducedMotion } from "framer-motion";

const pad = (n) => String(n).padStart(2, "0");

const Corners = () => (
  <>
    {["left-3 top-3 border-l border-t", "right-3 top-3 border-r border-t", "left-3 bottom-3 border-l border-b", "right-3 bottom-3 border-r border-b"].map((c) => (
      <span key={c} className={`absolute h-3 w-3 border-ink/40 ${c}`} aria-hidden="true" />
    ))}
  </>
);

const PlayMark = () => (
  <span className="absolute left-1/2 top-1/2 flex h-16 w-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-ink/50 transition-transform duration-700 group-hover:scale-110 sm:h-20 sm:w-20" aria-hidden="true">
    <span className="ml-1 h-0 w-0 border-y-[7px] border-l-[11px] border-y-transparent border-l-ink" />
  </span>
);

const Media = ({ src, embed, loop, video, poster, alt, position, blend }) => {
  const reduce = useReducedMotion();
  if (embed) return <iframe src={embed} title={alt} className="absolute inset-0 h-full w-full" allow="autoplay; fullscreen; picture-in-picture" allowFullScreen loading="lazy" />;
  if (video) return <video src={video} poster={poster} className="absolute inset-0 h-full w-full bg-black object-cover" controls playsInline preload="metadata" aria-label={alt} data-testid="final-video-player" />;
  if (loop) return <video src={loop} poster={poster} className="absolute inset-0 h-full w-full object-cover" autoPlay={!reduce} muted loop playsInline preload={reduce ? "none" : "auto"} aria-label={alt} />;
  if (src) return <img src={src} alt={alt} style={{ objectPosition: position }} className={`absolute inset-0 h-full w-full object-cover ${blend ? "mix-blend-multiply" : ""}`} loading="lazy" decoding="async" />;
  return null;
};

// Editable media frame: renders a labelled placeholder until src / embed / loop is provided.
export const Frame = ({ kind = "image", n, desc, ratio = "16/9", src, embed, loop, video, poster, alt, position, blend, className = "", innerClass = "", innerStyle, testId, dark = false }) => {
  const isVideo = kind === "video";
  const type = isVideo ? "Video" : "Image";
  const label = `${type}${n ? ` ${pad(n)}` : ""} — ${desc}, ${ratio.replace("/", ":")} — replace with original`;
  const filled = src || embed || loop || video;
  return (
    <figure className={`group/frame relative w-full overflow-hidden ${className}`} style={{ aspectRatio: ratio }} data-testid={testId}>
      <motion.div style={innerStyle} className={`absolute inset-0 ${dark ? "bg-[#202020]" : "bg-frame"} ${innerClass}`}>
        {filled ? (
          <Media src={src} embed={embed} loop={loop} video={video} poster={poster} alt={alt || desc} position={position} blend={blend} />
        ) : (
          <div role="img" aria-label={`Placeholder: ${desc}`} className="absolute inset-0">
            <Corners />
            <span className="absolute left-1/2 top-1/2 h-px w-10 -translate-x-1/2 bg-ink/25" aria-hidden="true" />
            <span className="absolute left-1/2 top-1/2 h-10 w-px -translate-y-1/2 bg-ink/25" aria-hidden="true" />
            {isVideo && <PlayMark />}
          </div>
        )}
      </motion.div>
      {!filled && (
        <figcaption className="pointer-events-none absolute inset-x-0 bottom-0 p-4 sm:p-5">
          <span className="label block max-w-[90%] normal-case leading-snug tracking-[0.02em] text-mute">{label}</span>
        </figcaption>
      )}
    </figure>
  );
};
